/**
 * D721: the longest search (`q`, characters after trimming) `GET /v1/support/help-articles` accepts - a longer one is refused `HELP_QUERY_TOO_LONG`. The wire
 * contract of the server's `HELP_SEARCH_MAX_LENGTH` (`@vello/case-management`); a vello-api test keeps the two equal, so a client's search box never allows
 * text the server refuses.
 */
export const HELP_ARTICLES_QUERY_MAX_LENGTH = 100;
export class SafetyApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** D692 (gap 6): the ride's safety check-in as the caller (the ride's rider or driver) reads it; `null` when nothing was asked. */
    safetyCheckIn(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/safety-check-in`, auth: true });
    }
    /**
     * D692 (gap 6): "Estou bem" (`OK`) or "Preciso de ajuda" (`HELP`, the SOS path - the device fix travels when the app has one). An answer is final
     * (`409 CHECK_IN_ALREADY_ANSWERED`); a closed check-in takes none (`409 CHECK_IN_CLOSED`).
     */
    answerSafetyCheckIn(rideId, checkInId, input) {
        return this.client.request({ method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/safety-check-in/${encodeURIComponent(checkInId)}/answer`, body: input, auth: true });
    }
    /** D692 (gap 13): the rider's standing choice of whom the trip is shared with automatically, and the version of the consent text. */
    tripSharePreference() {
        return this.client.request({ method: 'GET', path: '/v1/me/trip-share-preferences', auth: true });
    }
    /** D692 (gap 13): set it (`TRIP_SHARE_CONSENT_REQUIRED` without the consent, `TRUSTED_CONTACT_NOT_FOUND` for a contact that is not the rider's, `TRIP_SHARE_NO_CONTACTS`). */
    setTripSharePreference(input) {
        return this.client.request({ method: 'PUT', path: '/v1/me/trip-share-preferences', body: input, auth: true });
    }
    /** D692 (gap 13): complete the automatic share the platform could not send itself - the link for the phone's composer (once per ride). */
    completeAutoShare(rideId) {
        return this.client.request({ method: 'POST', path: `/v1/safety/rides/${encodeURIComponent(rideId)}/auto-share`, body: {}, auth: true });
    }
    /** D692 (gap 13): stop the ride's automatic share from the live trip (the link is revoked at once; idempotent). */
    stopAutoShare(rideId) {
        return this.client.request({ method: 'POST', path: `/v1/safety/rides/${encodeURIComponent(rideId)}/auto-share/stop`, body: {}, auth: true });
    }
    /** D692 (gap 18): whether the country of `market` lets the app offer recording (OFF until the owner publishes it after legal approval). */
    audioRecordingPolicy(market) {
        return this.client.request({ method: 'GET', path: `/v1/safety/audio-recording?market=${encodeURIComponent(market)}`, auth: true });
    }
    /**
     * D692 (gap 18): attach a recording to the caller's own safety report (`AUDIO_RECORDING_NOT_PERMITTED` 403 where the country has not allowed it, `AUDIO_CONSENT_REQUIRED`,
     * `AUDIO_RECORDING_INVALID`, `AUDIO_INCIDENT_WITHOUT_RIDE` / `_CLOSED`, `AUDIO_LIMIT_REACHED` 409). Returns the evidence id and when it expires - never the bytes.
     */
    attachAudio(incidentId, input) {
        return this.client.request({ method: 'POST', path: `/v1/safety/incidents/${encodeURIComponent(incidentId)}/audio`, body: input, auth: true });
    }
    /** D692 (gap 18): erase a recording the caller attached (`AUDIO_UNDER_LEGAL_HOLD` 409 while a hold applies). Idempotent. */
    eraseAudio(evidenceId) {
        return this.client.request({ method: 'DELETE', path: `/v1/safety/audio-evidence/${encodeURIComponent(evidenceId)}`, auth: true });
    }
    reportIncident(input) {
        return this.client.request({ method: 'POST', path: '/v1/safety/incidents', body: input, auth: true });
    }
    getIncident(incidentId) {
        return this.client.request({ method: 'GET', path: `/v1/safety/incidents/${encodeURIComponent(incidentId)}`, auth: true });
    }
    /** Share a live trip with a trusted contact via a time-bound, revocable, scoped grant (S103). */
    shareTrip(rideId, input) {
        return this.client.request({ method: 'POST', path: `/v1/safety/rides/${encodeURIComponent(rideId)}/shares`, body: input, auth: true });
    }
    /** Revoke a trip share the rider created (S103). Idempotent; owner-scoped on the server. */
    revokeShare(grantId) {
        return this.client.request({ method: 'POST', path: `/v1/safety/shares/${encodeURIComponent(grantId)}/revoke`, body: {}, auth: true });
    }
    /** D854 (D2-010): the ride's trip links that still work — the safety sheet reopens knowing it is sharing. */
    listTripShares(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/safety/rides/${encodeURIComponent(rideId)}/shares`, auth: true });
    }
    /** D854 (D2-010): "Parar de compartilhar" — stop every working link of the ride at once. Idempotent; owner-scoped. */
    revokeTripShares(rideId) {
        return this.client.request({ method: 'POST', path: `/v1/safety/rides/${encodeURIComponent(rideId)}/shares/revoke`, body: {}, auth: true });
    }
    /** D440 (R-03): the caller's trusted contacts. */
    listTrustedContacts() {
        return this.client.request({ method: 'GET', path: '/v1/me/trusted-contacts', auth: true });
    }
    /** D440 (R-03): add a trusted contact (`TRUSTED_CONTACTS_AT_CAPACITY` / `TRUSTED_CONTACT_DUPLICATE` / `TRUSTED_CONTACT_INVALID`). */
    addTrustedContact(input) {
        return this.client.request({ method: 'POST', path: '/v1/me/trusted-contacts', body: input, auth: true });
    }
    /** D440 (R-03): remove one of the caller's trusted contacts. */
    removeTrustedContact(contactId) {
        return this.client.request({ method: 'DELETE', path: `/v1/me/trusted-contacts/${encodeURIComponent(contactId)}`, auth: true });
    }
}
export class SupportApi {
    client;
    constructor(client) {
        this.client = client;
    }
    openTicket(input) {
        return this.client.request({ method: 'POST', path: '/v1/support/tickets', body: input, auth: true });
    }
    /** D448 (S-01): the topics the caller's persona may ask about (rider ≠ driver). */
    helpTopics() {
        return this.client.request({ method: 'GET', path: '/v1/support/help-topics', auth: true });
    }
    /** D721 (doc 29 G8): the Help home rules of a market - the last-ride card (age, quick issues per end state) and the verticals it offers. */
    helpHome(market) {
        return this.client.request({ method: 'GET', path: `/v1/support/help-home?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** D721: the published help articles of the caller's audience in the market (the preferred `locale` when its country serves it), by vertical, searched. */
    helpArticles(input) {
        const qs = new URLSearchParams({ market: input.market });
        if (input.locale !== undefined)
            qs.set('locale', input.locale);
        if (input.q !== undefined && input.q.trim() !== '')
            qs.set('q', input.q.trim());
        if (input.vertical !== undefined)
            qs.set('vertical', input.vertical);
        if (input.limit !== undefined)
            qs.set('limit', String(input.limit));
        return this.client.request({ method: 'GET', path: `/v1/support/help-articles?${qs.toString()}`, auth: true });
    }
    /** D448 (S-01): the tickets the caller filed, newest first. */
    myTickets() {
        return this.client.request({ method: 'GET', path: '/v1/support/my-tickets', auth: true });
    }
    /** D448 (S-01): one of the caller's tickets with its public thread. */
    myTicket(ticketId) {
        return this.client.request({ method: 'GET', path: `/v1/support/my-tickets/${encodeURIComponent(ticketId)}`, auth: true });
    }
    /** D448 (S-01): answer the team (`SUPPORT_TICKET_CLOSED` 409 on a closed ticket, `SUPPORT_REPLY_LIMIT` 429). */
    replyToMyTicket(ticketId, body) {
        return this.client.request({ method: 'POST', path: `/v1/support/my-tickets/${encodeURIComponent(ticketId)}/replies`, body: { body }, auth: true });
    }
}
//# sourceMappingURL=safety.js.map